import {
  Accordion,
  AccordionItem,
  Box,
  Button,
  DATE_FORMAT,
  HighlightIcon,
  Icon,
  type IconName,
} from '@dev-spendesk/grapes';
import React from 'react';

import { ContactUs } from 'common/components/ContactUs';
import { useTranslation } from 'common/hooks/useTranslation';
import { type PriceMigrationPlan } from 'modules/company/billing/components/CompanyPlan/hooks/useGetBillingMigration/useGetBillingMigration';
import { formatMonetaryValue } from 'src/core/utils/monetaryValue';

type Props = {
  migrationPlan: PriceMigrationPlan;
};

const reasonItems = [
  {
    id: 'reason1',
    key: 'billing.priceUpdateModal.2025PriceUpdate.whySection.reasons.item1' as const,
  },
  {
    id: 'reason2',
    key: 'billing.priceUpdateModal.2025PriceUpdate.whySection.reasons.item2' as const,
  },
  {
    id: 'reason3',
    key: 'billing.priceUpdateModal.2025PriceUpdate.whySection.reasons.item3' as const,
  },
  {
    id: 'reason4',
    key: 'billing.priceUpdateModal.2025PriceUpdate.whySection.reasons.item4' as const,
  },
  {
    id: 'reason5',
    key: 'billing.priceUpdateModal.2025PriceUpdate.whySection.reasons.item5' as const,
  },
];

const comingNextItems = [
  {
    id: 'nextItem1',
    iconName: 'globe-eu-africa' as IconName,
    iconVariant: 'pink' as const,
    titleKey:
      'billing.priceUpdateModal.2025PriceUpdate.nextSection.items.item1.title' as const,
    descriptionKey:
      'billing.priceUpdateModal.2025PriceUpdate.nextSection.items.item1.description' as const,
  },
  {
    id: 'nextItem2',
    iconName: 'card' as IconName,
    iconVariant: 'lemon' as const,
    titleKey:
      'billing.priceUpdateModal.2025PriceUpdate.nextSection.items.item2.title' as const,
    descriptionKey:
      'billing.priceUpdateModal.2025PriceUpdate.nextSection.items.item2.description' as const,
  },
  {
    id: 'nextItem3',
    iconName: 'shopping-bag' as IconName,
    iconVariant: 'blue' as const,
    titleKey:
      'billing.priceUpdateModal.2025PriceUpdate.nextSection.items.item3.title' as const,
    descriptionKey:
      'billing.priceUpdateModal.2025PriceUpdate.nextSection.items.item3.description' as const,
  },
  {
    id: 'nextItem4',
    iconName: 'square-3-stack-3d' as IconName,
    iconVariant: 'apricot' as const,
    titleKey:
      'billing.priceUpdateModal.2025PriceUpdate.nextSection.items.item4.title' as const,
    descriptionKey:
      'billing.priceUpdateModal.2025PriceUpdate.nextSection.items.item4.description' as const,
  },
];

const faqItems = [
  {
    id: 'faqItem1',
    questionKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item1.question' as const,
    answerKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item1.answer' as const,
  },
  {
    id: 'faqItem2',
    questionKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item2.question' as const,
    answerKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item2.answer' as const,
  },
  {
    id: 'faqItem3',
    questionKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item3.question' as const,
    answerKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item3.answer' as const,
  },
  {
    id: 'faqItem4',
    questionKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item4.question' as const,
    answerKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item4.answer' as const,
  },
  {
    id: 'faqItem5',
    questionKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item5.question' as const,
    answerKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item5.answer' as const,
  },
  {
    id: 'faqItem6',
    questionKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item6.question' as const,
    answerKey:
      'billing.priceUpdateModal.2025PriceUpdate.faqSection.item6.answer' as const,
  },
];

export const PricingUpdate2025Content = ({ migrationPlan }: Props) => {
  const { t, localeFormat } = useTranslation('global');
  const changeDate = new Date(migrationPlan.migrationDate);

  return (
    <div className="flex justify-center gap-48 px-[80px] py-16">
      <section className="w-[40vw] max-w-[1000px]">
        <h1 className="title-xxl mb-20">
          {t('billing.priceUpdateModal.2025PriceUpdate.title')}
        </h1>
        <p className="body-l">
          {t('billing.priceUpdateModal.2025PriceUpdate.description', {
            date: localeFormat(changeDate, DATE_FORMAT.CUSTOM, {
              dateStyle: 'medium',
              timeZone: 'Europe/Paris',
            }),
          })}
          <span className="text-content-brand-default">
            {' '}
            {t(
              'billing.priceUpdateModal.2025PriceUpdate.descriptionCommitment',
            )}
          </span>
        </p>
        <section className="mt-24">
          <h2 className="heading-l mb-16">
            {t('billing.priceUpdateModal.2025PriceUpdate.whySection.title')}
          </h2>
          <p className="text-content-secondary-bg-secondary mb-12">
            {t(
              'billing.priceUpdateModal.2025PriceUpdate.whySection.description',
            )}
          </p>
          <ul>
            {reasonItems.map((item) => (
              <li key={item.id} className="mt-8 flex items-center">
                <Icon
                  name="circle-check"
                  className="text-content-success-default mr-12 shrink-0"
                />
                {t(item.key)}
              </li>
            ))}
          </ul>
        </section>
        <section className="mt-24">
          <h2 className="heading-l mb-16">
            {t('billing.priceUpdateModal.2025PriceUpdate.nextSection.title')}
          </h2>
          <p className="text-content-secondary-bg-secondary mb-20">
            {t(
              'billing.priceUpdateModal.2025PriceUpdate.nextSection.description',
            )}
          </p>
          {comingNextItems.map((item) => (
            <div
              key={item.id}
              className="box mt-16 flex items-center gap-16 p-16"
            >
              <HighlightIcon
                name={item.iconName}
                size={40}
                variant={item.iconVariant}
                className="shrink-0"
              />
              <div>
                <span className="title-m">{t(item.titleKey)}</span>
                <p className="text-content-secondary-bg-secondary">
                  {t(item.descriptionKey)}
                </p>
              </div>
            </div>
          ))}
        </section>
        <section className="mt-24">
          <h2 className="heading-l mb-16">
            {t('billing.priceUpdateModal.2025PriceUpdate.faqSection.title')}
          </h2>
          <Accordion>
            {faqItems.map((faqItem) => (
              <AccordionItem
                key={faqItem.id}
                renderHeader={() => (
                  <div className="title-m">{t(faqItem.questionKey)}</div>
                )}
              >
                <div
                  className="text-content-secondary-bg-secondary px-24 py-12"
                  // eslint-disable-next-line react/no-danger
                  dangerouslySetInnerHTML={{
                    __html: t(faqItem.answerKey),
                  }}
                />
              </AccordionItem>
            ))}
          </Accordion>
        </section>
        <div className="rounded-8 bg-background-secondary-default mt-24 flex items-center p-16">
          <p className="title-m">
            {t('billing.priceUpdateModal.2025PriceUpdate.otherQuestion')}
          </p>
          <ContactUs>
            {({ handleContactUs }) => (
              <Button
                className="ml-auto whitespace-nowrap"
                variant="secondaryNeutral"
                text={t('helpCenter.contactUs')}
                onClick={() => {
                  handleContactUs();
                }}
              />
            )}
          </ContactUs>
        </div>
      </section>
      <section className="rounded-8 bg-background-secondary-default sticky top-0 h-fit w-[480px] min-w-[480px] p-24">
        <h3 className="title-l mb-20">{t('misc.details')}</h3>
        <div className="mb-20 flex justify-between">
          <span className="text-content-secondary-bg-secondary body-m">
            {t('billing.priceUpdateModal.2025PriceUpdate.currentPrice')}
          </span>
          <span className="title-xl font-bold">
            {formatMonetaryValue(migrationPlan.current)}
          </span>
        </div>
        <div className="mb-20 flex justify-between">
          <span className="text-content-secondary-bg-secondary">
            {t('billing.priceUpdateModal.2025PriceUpdate.newPrice')}
          </span>
          <span className="title-xl font-bold">
            {formatMonetaryValue(migrationPlan.estimate)}
          </span>
        </div>
        {migrationPlan.planItems && migrationPlan.planItems.length > 0 && (
          <Box variant="primary" className="mb-24 max-h-[250px] overflow-auto">
            <div className="text-content-primary title-m mb-16">
              {t('billing.priceUpdateModal.2025PriceUpdate.entityBreakdown')}
            </div>
            <ul className="flex flex-col gap-8">
              {migrationPlan.planItems.map((planItem) => (
                <li
                  key={planItem.companyId}
                  className="flex justify-between gap-4"
                >
                  <span className="text-content-secondary-bg-primary overflow-hidden text-nowrap text-ellipsis">
                    {planItem.companyName}
                  </span>
                  <span className="title-l">
                    {formatMonetaryValue(planItem.estimate)}
                  </span>
                </li>
              ))}
            </ul>
          </Box>
        )}
        <div>
          <span className="title-m">
            {t('billing.priceUpdateModal.2025PriceUpdate.note')}
          </span>
          <ul className="text-content-secondary-bg-secondary mt-8 list-disc px-24">
            <li>
              {t('billing.priceUpdateModal.2025PriceUpdate.priceIsEstimate')}
            </li>
            <li>
              {t('billing.priceUpdateModal.2025PriceUpdate.entitiesPrice')}
            </li>
            <li>
              {t('billing.priceUpdateModal.2025PriceUpdate.priceIsWithoutVat')}
            </li>
            {migrationPlan.planItems && migrationPlan.planItems.length > 0 && (
              <li>
                {t('billing.priceUpdateModal.2025PriceUpdate.conversion')}
              </li>
            )}
          </ul>
        </div>
        <div className="separator my-20" />
        <span className="title-m">
          {t('billing.priceUpdateModal.2025PriceUpdate.timeline')}
        </span>
        <div className="mt-8 flex justify-between gap-16">
          <Icon
            name="calendar-clock"
            size="l"
            className="text-content-brand-default"
          />
          <div className="w-fit">
            <span className="title-m">
              {localeFormat(changeDate, DATE_FORMAT.CUSTOM, {
                dateStyle: 'short',
                timeZone: 'Europe/Paris',
              })}
            </span>
            <p className="text-content-secondary-bg-secondary mt-8">
              {t('billing.priceUpdateModal.2025PriceUpdate.newPriceDate')}
            </p>
          </div>
        </div>
      </section>
    </div>
  );
};
